//@flow
import {
  assoc,
  both,
  compose,
  complement,
  difference,
  differenceWith,
  eqBy,
  findIndex,
  has,
  identity,
  insertAll,
  lensIndex,
  move,
  omit,
  over,
  pathEq,
  prop,
  propOr,
  propEq,
  reject,
  remove,
  set,
  sortBy,
  union,
  view,
  without,
  insert,
} from "ramda";
import { propNotEq } from "@quantum/utils";
import {
  type State,
  type Action,
  initialState,
  getGrupamentoMedidaSelecionado,
  getInitialEdicaoMedidaComMedida,
  getIdMedidaSelecionada,
  getPeriodoUnicoSelecionado,
  getInitialEdicaoMedida,
  getInitialEdicaoMedidaPersonalizada,
  getModoApp,
} from "./";
import Moment from "moment";
import {
  type DataBase,
  type MedidaSelecionada,
  type EdicaoMedida,
  type Id,
  type GrupamentosMedida,
  isMedidaNome,
  FORMA_VISUALIZACAO_SERIES_POR_ATIVOS,
  FORMATO_DATA_BANCO,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO,
  ID_TIPO_JANELA_MESES,
  ID_PERIODO_JANELA,
  ID_PERIODO_NOS_ULTIMOS_4_MESES,
  TAMANHO_DEFAULT_JANELA_TIPO_MESES,
  TAMANHO_DEFAULT_JANELA_TIPO_DIAS_UTEIS,
  GRUPAMENTO_MEDIDAS_QUALITATIVAS,
  GRUPAMENTO_MEDIDAS_SERIES,
  VALOR_DEFAULT_JANELA_MOVEL_VISIVEL,
  MODO_APP_SERIES,
} from "../model";
import {
  type AdicionarMedidaAction,
  getMedidasSelecionadas,
  getEdicaoMedida,
  gerarEdicaoMedida,
} from "../state";

import {
  campoArquivosMedidasLens,
  campoEdicaoMedidaLens,
  carregandoFuncaoQtlinkLens,
  dataBaseLens,
  funcaoQtlinkLens,
  medidasSelecionadasLens,
  medidasParaPersonalizarLens,
  seletoresAbertosLens,
  edicaoMedidaLens,
  gridAggregationState,
  gridSortState,
  gridFilterState,
  gridGroupState,
  gridFixedLeftState,
  gridFixedRightState,
  gridOrderState,
  gridState,
  isAxisLens,
  modoVisualizacaoSeriesLens,
} from "./lenses";
import { maybeEmpty, lazyFind } from "../util";
import {
  getDiasUteisPeriodicidadeNumeradorSelecionado,
  getPeriodicidadeEstatisticaNumeradorSelecionada,
  getRetornoNumeradorSelecionado,
  getIndiceMedidaEditada,
} from "./selectors/edicaoMedidaSelectors";
import { isModoSeries } from "./selectors/comum";
import { resetGridState, selecionarGrupamentoMedida } from "./actions";

const restaurarEdicaoMedidas = (state: State): State => {
  const medidas = getIdMedidaSelecionada(state);
  const modoApp = getModoApp(state);

  return compose(
    set(
      campoEdicaoMedidaLens(
        state.grupamentoMedidaSelecionado,
        "indiceMedidaEditada"
      ),
      null
    ),
    set(campoEdicaoMedidaLens(null, "indiceMedidaEditada"), null),
    set(campoEdicaoMedidaLens(null, "idGrupoMedidasSelecionadas"), null),
    set(
      edicaoMedidaLens(state.grupamentoMedidaSelecionado),
      getInitialEdicaoMedidaComMedida(
        state.grupamentoMedidaSelecionado === GRUPAMENTO_MEDIDAS_QUALITATIVAS ||
          (modoApp === MODO_APP_SERIES &&
            state.grupamentoMedidaSelecionado === GRUPAMENTO_MEDIDAS_SERIES)
          ? null
          : medidas,
        state
      )
    )
  )(state);
};

function editarMedida(
  state: State,
  indiceMedidaEditada: number,
  medidasSelecionadas: Array<MedidaSelecionada>
): State {
  const grupamentoMedidaSelecionado: string = getGrupamentoMedidaSelecionado(
    state
  );

  const medidaSendoEditada = state.medidasSelecionadas[indiceMedidaEditada];
  const medidasSelecionadasExcetoEditada: Array<MedidaSelecionada> = remove(
    indiceMedidaEditada,
    1,
    state.medidasSelecionadas
  );

  const medidasAAdicionar =
    grupamentoMedidaSelecionado === GRUPAMENTO_MEDIDAS_QUALITATIVAS
      ? differenceWith(
          eqBy(prop("descricaoSelecao")),
          medidasSelecionadas,
          // Remove as medidas do usuário da comparação, pois podemos ter homônimas com qualitativas
          filtrarMedida(
            medidasSelecionadasExcetoEditada,
            ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO
          )
        )
      : medidasSelecionadas;

  // Equivalente a um splice, porém mantendo a regra de imutabilidade do redux
  let medidasEditadas: Array<MedidaSelecionada> = insertAll(
    indiceMedidaEditada,
    medidasAAdicionar,
    medidasSelecionadasExcetoEditada
  );

  // Remover medidas derivadas (para serem substituídas)
  const edicaoMedida: EdicaoMedida = getEdicaoMedida(state);
  if (edicaoMedida.idGrupoMedidasSelecionadas) {
    const periodoUnicoSelecionado = getPeriodoUnicoSelecionado(state);

    if (
      periodoUnicoSelecionado &&
      periodoUnicoSelecionado === ID_PERIODO_JANELA &&
      medidaSendoEditada.tamanhoJanela !== edicaoMedida.tamanhoJanela &&
      medidaSendoEditada.quantidadeJanelas === edicaoMedida.quantidadeJanelas
    ) {
      const medidasNovasIndesejadas = medidasAAdicionar.filter(
        propNotEq("numeroJanela", medidaSendoEditada.numeroJanela)
      );

      medidasEditadas = without(medidasNovasIndesejadas, medidasEditadas);
    }

    medidasEditadas = medidasEditadas.filter(
      propNotEq(
        "idGrupoMedidasSelecionadas",
        edicaoMedida.idGrupoMedidasSelecionadas
      )
    );
  }

  return compose(
    gerarAliasesDasMedidasSelecionadas,
    set(medidasSelecionadasLens, medidasEditadas),
    restaurarEdicaoMedidas
  )(state);
}

function adicionarMedidas(state: State, action: AdicionarMedidaAction) {
  const medidas = action.medidasSelecionadas;

  if (action.substituirSelecao) {
    return gerarAliasesDasMedidasSelecionadas(
      restaurarEdicaoMedidas({
        ...state,
        medidasSelecionadas: medidas,
      })
    );
  } else {
    const grupamentoMedidaSelecionado: string = getGrupamentoMedidaSelecionado(
      state
    );
    const idMedidaSelecionada: ?Id = getIdMedidaSelecionada(state);
    const medidasSelecionadas: Array<MedidaSelecionada> = getMedidasSelecionadas(
      state
    );

    const medidasAAdicionar =
      grupamentoMedidaSelecionado === GRUPAMENTO_MEDIDAS_QUALITATIVAS
        ? differenceWith(
            eqBy(prop("descricaoSelecao")),
            medidas,
            // Remove as medidas do usuário da comparação, pois podemos ter homônimas com qualitativas
            filtrarMedida(
              medidasSelecionadas,
              ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO
            )
          )
        : medidas;

    const multiplasMedidas =
      medidasSelecionadas !== []
        ? medidas.filter(
            medidaChecked =>
              !medidasSelecionadas.find(medida => medida === medidaChecked)
          )
        : medidas;

    return compose(
      gerarAliasesDasMedidasSelecionadas,
      over(
        medidasSelecionadasLens,
        insertAll(
          action.posicao !== -1 ? action.posicao : medidasSelecionadas.length,
          medidasAAdicionar
        )
      ),
      set(
        edicaoMedidaLens(grupamentoMedidaSelecionado),
        getInitialEdicaoMedidaComMedida(
          grupamentoMedidaSelecionado === GRUPAMENTO_MEDIDAS_QUALITATIVAS ||
            multiplasMedidas.length > 1
            ? null
            : idMedidaSelecionada,
          state
        )
      )
    )(state);
  }
}

const geradorDeAliases = function*(inicial = 0) {
  const alias = indice => {
    const alfabeto = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const letra = alfabeto.charAt(indice % 26);
    const concatenado = Math.floor(indice / alfabeto.length);
    return concatenado > 0 ? alias(concatenado - 1) + letra : letra;
  };

  while (true) yield `Q${alias(inicial++)}`;
};

const gerarAliasesDasMedidasSelecionadas = (state: State): State => {
  return getMedidasSelecionadas(state).reduce((novoEstado, medida, i) => {
    const aliasesUsados = new Set(
      getMedidasSelecionadas(novoEstado)
        .map(medida => medida.alias)
        .filter(Boolean)
    );

    const aliasDisponivel = lazyFind(
      alias => !aliasesUsados.has(alias),
      geradorDeAliases()
    );

    const alias = maybeEmpty(medida.alias).getOrElse(aliasDisponivel);

    return set(
      compose(medidasSelecionadasLens, lensIndex(i)),
      {
        ...medida,
        alias,
      },
      novoEstado
    );
  }, state);
};

function swapMedidas(
  state: State,
  indiceOrigem: number,
  indiceDestino: number
) {
  return over(
    medidasSelecionadasLens,
    move(indiceOrigem, indiceDestino),
    state
  );
}

function alternarMedidaSelecionada(
  state,
  indiceMedida: number,
  isChecked: boolean
) {
  return over(
    medidasSelecionadasLens,
    medidas =>
      medidas.map((medida, i) => ({
        ...medida,
        isChecked:
          i === indiceMedida && !isMedidaNome(medida.medida)
            ? isChecked
            : medida.isChecked,
      })),
    state
  );
}

function renomearMedidaSelecionada(state, indiceMedida: number, label: string) {
  return over(
    compose(medidasSelecionadasLens, lensIndex(indiceMedida)),
    medida => ({
      ...medida,
      label: !isMedidaNome(medida.medida) ? label : medida.label,
    }),
    state
  );
}

function restaurarNomePadraoDeMedidaSelecionada(state, indiceMedida: number) {
  return over(
    compose(medidasSelecionadasLens, lensIndex(indiceMedida)),
    medida => ({
      ...medida,
      label: !isMedidaNome(medida.medida) ? null : medida.label,
    }),
    state
  );
}

const alterarDataBaseSelecionada = (
  dataBase: DataBase,
  state: State
): State => {
  const novasMedidas = alteraDataBaseMedidas(state, dataBase);
  return compose(
    set(dataBaseLens, dataBase),
    set(medidasSelecionadasLens, novasMedidas)
  )(state);
};

function setCampoEdicaoMedida(state: State, campo: string, valor: any) {
  const grupamento = getGrupamentoMedidaSelecionado(state);
  return compose(
    set(campoEdicaoMedidaLens(grupamento, campo), valor),
    set(campoEdicaoMedidaLens("GLOBAL", campo), valor)
  )(state);
}

function overCampoEdicaoMedida(state: State, campo: string, fn: Function) {
  const grupamento = getGrupamentoMedidaSelecionado(state);
  return compose(
    over(campoEdicaoMedidaLens(grupamento, campo), fn),
    over(campoEdicaoMedidaLens("GLOBAL", campo), fn)
  )(state);
}

export function reducer(state: State = initialState, action: Action): State {
  const grupamento = getGrupamentoMedidaSelecionado(state);

  switch (action.type) {
    case "CARREGAR_APP":
      return { ...state, erroInicializacao: null };

    case "INICIAR_APP":
      return { ...state, appIniciada: true };

    case "SET_ERRO_INICIALIZACAO":
      return { ...state, erroInicializacao: action.erro };

    case "CARREGAR_DOMINIO":
      return { ...state, dominio: action.dominio };

    case "DEFINIR_MODO_APP": {
      let newState = { ...state, modoApp: action.payload };

      newState = {
        ...newState,
        edicaoMedida: {
          ...newState.edicaoMedida,
          GLOBAL: getInitialEdicaoMedida(newState),
          PERSONALIZADAS: getInitialEdicaoMedidaPersonalizada(newState),
        },
      };

      return isModoSeries(newState)
        ? reducer(
            newState,
            selecionarGrupamentoMedida(GRUPAMENTO_MEDIDAS_SERIES, true)
          )
        : newState;
    }

    case "SELECIONAR_MEDIDA":
      let edicaoMedidaGrupamento = Array.isArray(action.idMedidaSelecionada)
        ? {
            ...state.edicaoMedida[grupamento],
            medida: action.idMedidaSelecionada,
          }
        : getInitialEdicaoMedidaComMedida(action.idMedidaSelecionada, state);
      const indiceMedidaEditada = getIndiceMedidaEditada(state);

      if (
        typeof indiceMedidaEditada === "number" &&
        !Array.isArray(action.idMedidaSelecionada)
      ) {
        const medidaEditada = getEdicaoMedida(state);
        edicaoMedidaGrupamento = {
          ...medidaEditada,
          alias: edicaoMedidaGrupamento.alias,
          idGrupoMedidasSelecionadas:
            edicaoMedidaGrupamento.idGrupoMedidasSelecionadas,
          idMedidaUsuario: edicaoMedidaGrupamento.idMedidaUsuario,
          indiceMedidaEditada: edicaoMedidaGrupamento.indiceMedidaEditada,
          medida: edicaoMedidaGrupamento.medida,
          valorComboMedidas: edicaoMedidaGrupamento.valorComboMedidas,
        };
      }

      return {
        ...state,
        edicaoMedida: {
          ...state.edicaoMedida,
          [grupamento]: {
            ...edicaoMedidaGrupamento,
            indiceMedidaEditada: state.edicaoMedida.indiceMedidaEditada,
            idGrupoMedidasSelecionadas:
              state.edicaoMedida.idGrupoMedidasSelecionadas,
          },
        },
      };

    case "SELECIONAR_MEDIDA_USUARIO":
      return setCampoEdicaoMedida(
        state,
        "idMedidaUsuario",
        action.idMedidaUsuario
      );

    case "CARREGAR_MEDIDAS_USUARIO":
      return { ...state, medidasUsuario: action.medidasUsuario };

    case "ADICIONAR_MEDIDA_PARA_PERSONALIZAR": {
      const { ativo, medida } = action.medidaParaPersonalizar;

      const indiceMedidaSendoEditada = findIndex(
        ({ alias }) => medida.alias === alias,
        state.medidasSelecionadas
      );

      let medidaSendoEditada =
        state.medidasSelecionadas[indiceMedidaSendoEditada];
      const medidasSelecionadasExcetoSendoEditada = remove(
        indiceMedidaSendoEditada,
        1,
        state.medidasSelecionadas
      );

      if (ativo) {
        medidaSendoEditada = {
          ...medidaSendoEditada,
          ativos: union(medidaSendoEditada.ativos, [
            `${ativo.alias}:'${ativo.tipoItemSelecionavel}:"${ativo.nome}"'`,
          ]),
        };
      }

      const medidasEditadas = insert(
        indiceMedidaSendoEditada,
        medidaSendoEditada,
        medidasSelecionadasExcetoSendoEditada
      );

      return {
        ...state,
        medidasSelecionadas: medidasEditadas,
        medidasParaPersonalizar: union(state.medidasParaPersonalizar, [
          action.medidaParaPersonalizar,
        ]),
      };
    }

    case "ADICIONAR_MEDIDAS_PARA_PERSONALIZAR": {
      return {
        ...state,
        medidasParaPersonalizar: action.medidasParaPersonalizar,
      };
    }

    case "REMOVER_MEDIDA_PARA_PERSONALIZAR": {
      const indiceMedidaSendoRemovida = findIndex(
        ({ id }) => id === action.idMedidaParaPersonalizar,
        state.medidasParaPersonalizar
      );

      const medidaSendoRemovida =
        state.medidasParaPersonalizar[indiceMedidaSendoRemovida];

      const medidasParaPersonalizarSemRemovida = remove(
        indiceMedidaSendoRemovida,
        1,
        state.medidasParaPersonalizar
      );

      let medidasSelecionadas = state.medidasSelecionadas.slice();

      const { ativo } = medidaSendoRemovida;

      if (ativo) {
        const indiceMedidaSelecionadaSendoEditada = findIndex(
          ({ alias }) => alias === medidaSendoRemovida.medida.alias,
          state.medidasSelecionadas
        );

        let medidaSelecionadaSendoEditada =
          state.medidasSelecionadas[indiceMedidaSelecionadaSendoEditada];

        const medidasSelecionadasExcetoEditada = remove(
          indiceMedidaSelecionadaSendoEditada,
          1,
          state.medidasSelecionadas
        );

        medidaSelecionadaSendoEditada = {
          ...medidaSelecionadaSendoEditada,
          ativos: propOr([], "ativos", medidaSelecionadaSendoEditada).filter(
            key =>
              key !==
              `${ativo.alias}:'${ativo.tipoItemSelecionavel}:"${ativo.nome}"'`
          ),
        };

        medidasSelecionadas = insert(
          indiceMedidaSelecionadaSendoEditada,
          medidaSelecionadaSendoEditada,
          medidasSelecionadasExcetoEditada
        );
      }

      return {
        ...state,
        medidasParaPersonalizar: medidasParaPersonalizarSemRemovida,
        medidasSelecionadas,
      };
    }
    case "SET_SELETOR_ABERTO": {
      const { id, aberto } = action.payload;
      return set(seletoresAbertosLens(id), aberto, state);
    }

    case "SELECIONAR_DATA_BASE":
      return alterarDataBaseSelecionada(
        Moment(action.dataBaseSelecionada).format(FORMATO_DATA_BANCO),
        state
      );

    case "SELECIONAR_DATA_BASE_DEFAULT":
      return alterarDataBaseSelecionada(state.dominio.dataBaseDefault, state);

    case "SELECIONAR_PERIODO":
      return setCampoEdicaoMedida(
        state,
        "periodos",
        action.periodosSelecionados
      );

    case "SELECIONAR_AJUSTE_DIAS":
      return setCampoEdicaoMedida(
        state,
        "ajusteDias",
        action.ajusteDiasSelecionado
      );

    case "SELECIONAR_FONTE_FIDC":
      return setCampoEdicaoMedida(
        state,
        "fonteFidc",
        action.fonteFidcSelecionada
      );

    case "SELECIONAR_EXIBIR_CONFIGURACOES_AVANCADAS":
      const configAvancada = action.exibirConfiguracoesAvancadas;
      return compose(
        state =>
          setCampoEdicaoMedida(
            state,
            "exibirConfiguracoesAvancadas",
            configAvancada
          ),
        state =>
          setCampoEdicaoMedida(
            state,
            "janelaMovel",
            configAvancada ? VALOR_DEFAULT_JANELA_MOVEL_VISIVEL : 1
          )
      )(state);

    case "SELECIONAR_PERIODICIDADE":
      return setCampoEdicaoMedida(
        state,
        "periodicidade",
        action.periodicidadeSelecionada
      );

    case "SELECIONAR_TIPO_CONTA":
      return setCampoEdicaoMedida(
        state,
        "tipoConta",
        action.tipoContaSelecionado
      );

    case "SELECIONAR_VERSAO_CONTA":
      return setCampoEdicaoMedida(
        state,
        "versaoConta",
        action.versaoContaSelecionada
      );

    case "SELECIONAR_FORMA_ACUMULACAO":
      return setCampoEdicaoMedida(
        state,
        "formaAcumulacao",
        action.formaAcumulacaoSelecionada
      );

    case "SELECIONAR_CAPITAL_UTILIZADO":
      return setCampoEdicaoMedida(
        state,
        "capitalUtilizado",
        action.capitalUtilizadoSelecionado
      );

    case "SELECIONAR_TOLERANCIA_DF":
      return setCampoEdicaoMedida(state, "toleranciaDF", action.toleranciaDF);

    case "SELECIONAR_ANOS":
      return setCampoEdicaoMedida(state, "anos", action.anos);

    case "SELECIONAR_TIPO_REFERENCIA":
      return setCampoEdicaoMedida(
        state,
        "tipoReferencia",
        action.tipoReferencia
      );

    case "SELECIONAR_TRIMESTRE_REFERENCIA":
      return setCampoEdicaoMedida(
        state,
        "trimestreReferencia",
        action.trimestreReferencia
      );

    case "SELECIONAR_ANO_REFERENCIA":
      return setCampoEdicaoMedida(state, "anoReferencia", action.anoReferencia);

    case "SELECIONAR_COMPARACAO":
      return setCampoEdicaoMedida(state, "comparacao", action.comparacao);

    case "EDITAR_LAMBDA":
      return setCampoEdicaoMedida(state, "lambda", action.lambda);

    case "EDITAR_LIMITE":
      return setCampoEdicaoMedida(state, "limite", action.limite);

    case "SELECIONAR_ATIVO_SEM_RISCO":
      return setCampoEdicaoMedida(
        state,
        "ativoSemRisco",
        action.ativoSemRiscoSelecionado
      );

    case "SELECIONAR_PRIMEIRO_ATIVO_SEM_RISCO":
      return setCampoEdicaoMedida(
        state,
        "ativoSemRisco",
        getPrimeiroIndice(state)
      );

    case "SELECIONAR_TAXA_DE_ADMINISTRACAO":
      return setCampoEdicaoMedida(
        state,
        "taxaDeAdministracao",
        action.taxaDeAdministracaoSelecionada
      );

    case "EDITAR_HORIZONTE_DU":
      return setCampoEdicaoMedida(state, "horizonte", action.horizonte);

    case "EDITAR_CONFIANCA":
      return setCampoEdicaoMedida(state, "confianca", action.confianca);

    case "SELECIONAR_VOLATILIDADE":
      return setCampoEdicaoMedida(
        state,
        "volatilidade",
        action.volatilidadeSelecionada
      );

    case "SELECIONAR_TIPO_DO_RETORNO":
      return setCampoEdicaoMedida(
        state,
        "tipoDoRetorno",
        action.tipoDoRetornoSelecionado
      );

    case "SELECIONAR_BENCHMARK":
      return setCampoEdicaoMedida(
        state,
        "benchmark",
        action.benchmarkSelecionado
      );

    case "SELECIONAR_PRIMEIRO_BENCHMARK":
      return setCampoEdicaoMedida(state, "benchmark", getPrimeiroIndice(state));

    case "SELECIONAR_COMPETENCIA":
      return setCampoEdicaoMedida(state, "competencia", action.competencia);

    case "SELECIONAR_PRIMEIRA_COMPETENCIA":
      return setCampoEdicaoMedida(
        state,
        "competencia",
        getPrimeiroMesAnoComposto(state)
      );

    case "SELECIONAR_INDICE":
      return setCampoEdicaoMedida(state, "indice", action.indice);

    case "SELECIONAR_INDICES":
      return setCampoEdicaoMedida(state, "indices", action.indices);

    case "SELECIONAR_PRIMEIRO_INDICE":
      return setCampoEdicaoMedida(state, "indice", getPrimeiroIndice(state));

    case "SELECIONAR_FREQUENCIAS":
      return setCampoEdicaoMedida(
        state,
        "frequencias",
        sortBy(identity, action.frequencias)
      );

    case "SELECIONAR_ULTIMA_NEGOCIACAO_MES":
      return setCampoEdicaoMedida(state, "ultimaNegociacaoMes", action.payload);

    case "SELECIONAR_ADICAO_DATA_REFERENCIA":
      return setCampoEdicaoMedida(
        state,
        "adicionarDataReferencia",
        action.payload
      );

    case "SELECIONAR_AJUSTES_PRECO":
      return setCampoEdicaoMedida(
        state,
        "ajustesPreco",
        sortBy(identity, action.ajustesPreco)
      );

    case "SELECIONAR_AJUSTES_PRECO_SEM_PROVENTOS":
      return setCampoEdicaoMedida(
        state,
        "ajustesPrecoSemProventos",
        sortBy(identity, action.ajustesPrecoSemProventos)
      );

    case "SELECIONAR_PL_UTILIZADO":
      return setCampoEdicaoMedida(state, "plUtilizado", action.plUtilizado);

    case "SELECIONAR_TOLERANCIA_DIAS_VALOR_MERCADO":
      return setCampoEdicaoMedida(
        state,
        "toleranciaDiasValorMercado",
        action.toleranciaDiasValorMercadoSelecionada
      );

    case "SELECIONAR_CORRETOR_MONETARIO":
      return setCampoEdicaoMedida(
        state,
        "corretorMonetario",
        action.corretorMonetarioSelecionado
      );

    case "SELECIONAR_DEFLATOR":
      return setCampoEdicaoMedida(
        state,
        "deflator",
        action.deflatorSelecionado
      );

    case "SELECIONAR_VALOR_BASE_DEFLATOR":
      return setCampoEdicaoMedida(
        state,
        "valorBaseDeflator",
        action.valorBaseDeflatorSelecionado
      );

    case "SELECIONAR_VALOR_BASE_DEFLATOR_OUTRO":
      return setCampoEdicaoMedida(
        state,
        "valorBaseDeflatorOutro",
        action.valorBaseDeflatorOutroSelecionado
      );

    case "SELECIONAR_DATA_BASE_DEFLATOR":
      return setCampoEdicaoMedida(
        state,
        "dataBaseDeflator",
        action.dataBaseDeflatorSelecionado
      );

    case "SELECIONAR_DATA_BASE_DEFLATOR_DATA_ESPECIFICA":
      return setCampoEdicaoMedida(
        state,
        "dataBaseDeflatorDataEspecifica",
        action.dataBaseDeflatorDataEspecificaSelecionada
      );

    case "SELECIONAR_DATA_ESPECIFICA":
      return setCampoEdicaoMedida(
        state,
        "dataEspecifica",
        action.dataEspecificaSelecionada
      );

    case "SELECIONAR_DATA_ESPECIFICA_DEFAULT":
      return setCampoEdicaoMedida(
        state,
        "dataEspecifica",
        Moment(state.dominio.dataBaseDefault, FORMATO_DATA_BANCO)
      );

    case "SELECIONAR_DATA_INICIAL_COMPOSTA":
      return setCampoEdicaoMedida(
        state,
        "dataInicialComposta",
        action.dataInicialCompostaSelecionada
      );

    case "SELECIONAR_PRIMEIRA_DATA_INICIAL_COMPOSTA":
      return setCampoEdicaoMedida(
        state,
        "dataInicialComposta",
        getPrimeiroMesAnoComposto(state)
      );

    case "SELECIONAR_DATA_FINAL_COMPOSTA":
      return setCampoEdicaoMedida(
        state,
        "dataFinalComposta",
        action.dataFinalCompostaSelecionada
      );

    case "SELECIONAR_PRIMEIRA_DATA_FINAL_COMPOSTA":
      return setCampoEdicaoMedida(
        state,
        "dataFinalComposta",
        getPrimeiroMesAnoComposto(state)
      );

    case "SELECIONAR_DATA_INICIAL":
      return setCampoEdicaoMedida(
        state,
        "dataInicial",
        action.dataInicialSelecionada
      );

    case "SELECIONAR_DATA_FINAL":
      return setCampoEdicaoMedida(
        state,
        "dataFinal",
        action.dataFinalSelecionada
      );

    case "SELECIONAR_JANELA_MOVEL":
      return setCampoEdicaoMedida(state, "janelaMovel", action.janelaMovel);

    case "SELECIONAR_TAMANHO_DA_JANELA":
      return setCampoEdicaoMedida(
        state,
        "tamanhoJanela",
        action.tamanhoJanelaSelecionada
      );

    case "SELECIONAR_QUANTIDADE_JANELAS":
      return setCampoEdicaoMedida(
        state,
        "quantidadeJanelas",
        action.quantidadeJanelasSelecionada
      );

    case "SELECIONAR_TIPO_JANELA": {
      state = setCampoEdicaoMedida(
        state,
        "tamanhoJanela",
        action.tipoJanelaSelecionada === ID_TIPO_JANELA_MESES
          ? TAMANHO_DEFAULT_JANELA_TIPO_MESES
          : TAMANHO_DEFAULT_JANELA_TIPO_DIAS_UTEIS
      );
      return setCampoEdicaoMedida(
        state,
        "tipoJanela",
        action.tipoJanelaSelecionada
      );
    }

    case "SELECIONA_COLUNAS_RANKING":
      return setCampoEdicaoMedida(state, "colunasRanking", action.colunas);

    case "SELECIONA_PESO_RANKING":
      return overCampoEdicaoMedida(state, "pesosRanking", pesos =>
        action.peso
          ? { ...pesos, [action.alias]: action.peso }
          : omit([action.alias], pesos)
      );

    case "SELECIONA_NOME_MEDIDA_COMPOSTA":
      return setCampoEdicaoMedida(state, "nome", action.nome);

    case "SELECIONA_FORMATO_MEDIDA_COMPOSTA":
      return setCampoEdicaoMedida(state, "formato", action.formato);

    case "SELECIONA_EXPRESSAO":
      return setCampoEdicaoMedida(state, "expressao", action.expressao);

    case "SELECIONAR_ATIVOS_CARTEIRA":
      return setCampoEdicaoMedida(state, "ativos", action.ativos);

    case "SELECIONAR_CAMPOS":
      return setCampoEdicaoMedida(state, "campos", action.campos);

    case "EDITAR_COMPRADORES":
      return setCampoEdicaoMedida(state, "compradores", action.compradores);

    case "SELECIONAR_FILTROS_TIPO_ATIVO":
      return {
        ...state,
        filtrosTipoAtivoSelecionados: union(
          state.filtrosTipoAtivoSelecionados,
          action.filtrosSelecionados
        ),
      };

    case "DESELECIONAR_FILTROS_TIPO_ATIVO":
      return {
        ...state,
        filtrosTipoAtivoSelecionados: difference(
          state.filtrosTipoAtivoSelecionados,
          action.filtrosDeselecionados
        ),
      };

    case "SELECIONAR_GRUPAMENTO_MEDIDA":
      return {
        ...state,
        edicaoMedida: {
          ...state.edicaoMedida,
          [action.grupamento]: {
            ...state.edicaoMedida[action.grupamento],
            indiceMedidaEditada: state.edicaoMedida.indiceMedidaEditada,
            idGrupoMedidasSelecionadas:
              state.edicaoMedida.idGrupoMedidasSelecionadas,
          },
        },
        grupamentoMedidaSelecionado: action.grupamento,
        alterouGrupamentoMedidaSelecionado: action.suprimirFlagAlteracao
          ? state.alterouGrupamentoMedidaSelecionado
          : state.alterouGrupamentoMedidaSelecionado ||
            state.grupamentoMedidaSelecionado !== action.grupamento,
      };

    case "TROCAR_MEDIDA_DE_POSICAO": {
      return swapMedidas(state, action.indiceOrigem, action.indiceDestino);
    }

    case "MARCAR_MEDIDA_SELECIONADA": {
      return alternarMedidaSelecionada(state, action.indiceMedida, true);
    }

    case "RENOMEAR_MEDIDA_SELECIONADA": {
      return renomearMedidaSelecionada(
        state,
        action.indiceMedida,
        action.label
      );
    }

    case "RESTAURAR_NOME_PADRAO_DE_MEDIDA_SELECIONADA": {
      return restaurarNomePadraoDeMedidaSelecionada(state, action.indiceMedida);
    }

    case "DESMARCAR_MEDIDA_SELECIONADA": {
      return alternarMedidaSelecionada(state, action.indiceMedida, false);
    }

    case "MARCAR_TODAS_MEDIDAS_SELECIONADAS": {
      return view(medidasSelecionadasLens, state)
        .map((medida, indice) => indice)
        .reduce(
          (state, indice) => alternarMedidaSelecionada(state, indice, true),
          state
        );
    }

    case "DESMARCAR_TODAS_MEDIDAS_SELECIONADAS": {
      return view(medidasSelecionadasLens, state)
        .map((medida, indice) => indice)
        .reduce(
          (state, indice) => alternarMedidaSelecionada(state, indice, false),
          state
        );
    }

    case "EXCLUIR_MEDIDAS_MARCADAS": {
      return over(
        medidasSelecionadasLens,
        action.remocaoLogica ? identity : reject(propEq("isChecked", true)),
        state
      );
    }

    case "SELECIONAR_ATIVOS": {
      return {
        ...state,
        ativosSelecionados: action.ativos,
      };
    }

    case "EDITAR_MEDIDA_SELECIONADA": {
      const grupamentoMedidaSelecionado: GrupamentosMedida =
        state.medidasSelecionadas[action.indiceMedida].medida.grupamentoMedida
          .nome;

      const filtros =
        state.medidasSelecionadas[action.indiceMedida].medida.filtros;

      const edicaoMedidaGrupamento = gerarEdicaoMedida(
        state,
        action.indiceMedida
      );

      return {
        ...state,
        edicaoMedida: {
          ...state.edicaoMedida,

          indiceMedidaEditada: edicaoMedidaGrupamento.indiceMedidaEditada,
          idGrupoMedidasSelecionadas:
            edicaoMedidaGrupamento.idGrupoMedidasSelecionadas,

          [grupamentoMedidaSelecionado]: edicaoMedidaGrupamento,
        },
        grupamentoMedidaSelecionado,
        filtrosTipoAtivoSelecionados: union(
          state.filtrosTipoAtivoSelecionados,
          filtros
        ),
      };
    }

    case "EXCLUIR_MEDIDA_SELECIONADA": {
      return over(
        medidasSelecionadasLens,
        action.remocaoLogica ? identity : remove(action.indiceMedida, 1),
        state
      );
    }

    case "SELECIONAR_VALOR_COMBO_MEDIDAS":
      return setCampoEdicaoMedida(state, "valorComboMedidas", action.valor);

    case "EXPORTAR_FUNCAO_QTLINK":
      return set(carregandoFuncaoQtlinkLens, true, state);

    case "EXPORTAR_FUNCAO_QTLINK_SUCCESS": {
      return compose(
        set(carregandoFuncaoQtlinkLens, false),
        set(funcaoQtlinkLens, action.funcaoQtLink)
      )(state);
    }

    case "EXPORTAR_FUNCAO_QTLINK_ERRO":
      return set(carregandoFuncaoQtlinkLens, false, state);

    case "ADICIONAR_MEDIDA": {
      const indiceMedidaEditada: ?number = getEdicaoMedida(state)
        .indiceMedidaEditada;

      if (indiceMedidaEditada != null && !action.substituirSelecao) {
        if (action.novaMedida) {
          return adicionarMedidas(state, action);
        } else {
          return editarMedida(
            state,
            indiceMedidaEditada,
            action.medidasSelecionadas
          );
        }
      } else if (indiceMedidaEditada != null && action.substituirSelecao) {
        return restaurarEdicaoMedidas(adicionarMedidas(state, action));
      } else {
        return adicionarMedidas(state, action);
      }
    }

    case "ABRIR_PAGINA_ARQUIVOS": {
      return {
        ...state,
        arquivosMedidas: {
          ...state.arquivosMedidas,
          operacao: action.operacao,
          extraInfoOperacao: action.extraInfo,
        },
      };
    }

    case "ABRIR_ARQUIVO": {
      return compose(
        set(campoArquivosMedidasLens("arquivoEmCache"), action.arquivo),
        set(campoArquivosMedidasLens("carregandoArquivo"), true),
        restaurarEdicaoMedidas
      )(state);
    }

    case "ABRIR_ARQUIVO_SUCCESS":
    case "ABERTURA_DE_ARQUIVO_CANCELADA": {
      return {
        ...state,
        arquivosMedidas: {
          ...state.arquivosMedidas,
          carregandoArquivo: false,
        },
      };
    }

    case "ERRO_AO_ABRIR_ARQUIVO": {
      return {
        ...state,
        arquivosMedidas: {
          ...state.arquivosMedidas,
          arquivoEmCache: null,
          carregandoArquivo: false,
        },
      };
    }

    case "LIMPAR_SELECAO": {
      return compose(
        set(campoArquivosMedidasLens("arquivoEmCache"), null),
        set(medidasParaPersonalizarLens, []),
        restaurarEdicaoMedidas
      )(state);
    }

    case "DEFINE_CHAVES_I18N": {
      return {
        ...state,
        i18n: action.chaves,
      };
    }

    case "OBTER_DETALHES_USUARIO_SUCCESS": {
      return {
        ...state,
        usuario: action.detalhes,
      };
    }

    case "SELECIONAR_TIPO_CALCULO": {
      return setCampoEdicaoMedida(state, "tipoCalculo", action.tipoCalculo);
    }

    case "SALVAR_ARQUIVO":
      return {
        ...state,
        arquivosMedidas: {
          ...state.arquivosMedidas,
          salvandoArquivo: true,
        },
      };

    case "ARQUIVO_SALVO":
    case "ERRO_AO_SALVAR_ARQUIVO":
      return {
        ...state,
        arquivosMedidas: {
          ...state.arquivosMedidas,
          salvandoArquivo: false,
        },
      };

    case "SELECIONAR_FECHAMENTO_DE_MES": {
      return {
        ...state,
        isFechamentoDeMes: action.checked,
      };
    }

    case "SET_MEDIDAS_SELECIONADAS_DESABILITADO": {
      return {
        ...state,
        medidasSelecionadasDesabilitada: action.desabilitado,
      };
    }

    case "SELECIONAR_VISUALIZACAO_CARTEIRA": {
      return setCampoEdicaoMedida(
        state,
        "visualizacaoCarteira",
        action.visualizacaoCarteira
      );
    }

    case "SELECIONAR_RETORNO_NUMERADOR": {
      state = setCampoEdicaoMedida(
        state,
        "retornoNumerador",
        action.retornoNumerador
      );
      if (getRetornoNumeradorSelecionado(state) === "ACUMULADO") {
        state = setCampoEdicaoMedida(
          state,
          "periodicidadeVolatilidade",
          getPeriodicidadeEstatisticaNumeradorSelecionada(state)
        );
        state = setCampoEdicaoMedida(
          state,
          "diasUteisPeriodicidadeVolatilidade",
          getDiasUteisPeriodicidadeNumeradorSelecionado(state)
        );
      }
      return state;
    }

    case "SELECIONAR_CALCULO_NUMERADOR": {
      return setCampoEdicaoMedida(
        state,
        "calculoNumerador",
        action.calculoNumerador
      );
    }

    case "SELECIONAR_PERIODICIDADE_ESTATISTICA_NUMERADOR": {
      if (getRetornoNumeradorSelecionado(state) === "ACUMULADO") {
        state = setCampoEdicaoMedida(
          state,
          "periodicidadeVolatilidade",
          action.periodicidadeNumerador
        );
      }
      return setCampoEdicaoMedida(
        state,
        "periodicidadeNumerador",
        action.periodicidadeNumerador
      );
    }

    case "EDITAR_DIAS_UTEIS_PERIODICIADE_NUMERADOR": {
      if (getRetornoNumeradorSelecionado(state) === "ACUMULADO") {
        state = setCampoEdicaoMedida(
          state,
          "diasUteisPeriodicidadeVolatilidade",
          action.diasUteisPeriodicidadeNumerador
        );
      }
      return setCampoEdicaoMedida(
        state,
        "diasUteisPeriodicidadeNumerador",
        action.diasUteisPeriodicidadeNumerador
      );
    }

    case "SELECIONAR_TIPO_VOLATILIDADE": {
      return setCampoEdicaoMedida(
        state,
        "tipoVolatilidade",
        action.tipoVolatilidade
      );
    }

    case "SELECIONAR_ESTIMACAO_VOLATILIDADE": {
      return setCampoEdicaoMedida(
        state,
        "estimacaoVolatilidade",
        action.estimacaoVolatilidade
      );
    }

    case "SELECIONAR_PERIODICIDADE_ESTATISTICA_VOLATILIDADE": {
      if (getRetornoNumeradorSelecionado(state) === "ACUMULADO") {
        state = setCampoEdicaoMedida(
          state,
          "periodicidadeNumerador",
          action.periodicidadeVolatilidade
        );
      }
      return setCampoEdicaoMedida(
        state,
        "periodicidadeVolatilidade",
        action.periodicidadeVolatilidade
      );
    }

    case "EDITAR_DIAS_UTEIS_PERIODICIADE_VOLATILIDADE": {
      if (getRetornoNumeradorSelecionado(state) === "ACUMULADO") {
        state = setCampoEdicaoMedida(
          state,
          "diasUteisPeriodicidadeNumerador",
          action.diasUteisPeriodicidadeVolatilidade
        );
      }
      return setCampoEdicaoMedida(
        state,
        "diasUteisPeriodicidadeVolatilidade",
        action.diasUteisPeriodicidadeVolatilidade
      );
    }

    case "SELECIONAR_TIPO_RETORNO_ESTATISTICA": {
      return setCampoEdicaoMedida(
        state,
        "tipoRetornoConfiguracoesEstatistica",
        action.tipoRetornoConfiguracoesEstatistica
      );
    }

    case "DEFINIR_MEDIDAS_FIXAS": {
      return {
        ...state,
        medidasFixas: action.medidas,
      };
    }

    case "EXCEL_DEFINIR_MEDIDAS_SUPORTADAS": {
      return {
        ...state,
        integracaoExcel: {
          ...state.integracaoExcel,
          medidasSuportadas: new Set(action.medidas),
        },
      };
    }

    case "EXCEL_DEFINIR_JWT": {
      return {
        ...state,
        integracaoExcel: {
          ...state.integracaoExcel,
          jwt: action.jwt,
        },
      };
    }

    case "EXCEL_EDITAR_ATIVO_EXCEL": {
      return {
        ...state,
        integracaoExcel: {
          ...state.integracaoExcel,
          ativo: action.ativo,
        },
      };
    }

    case "EXCEL_EDITAR_MODO_ATIVO_BUSCA": {
      return {
        ...state,
        integracaoExcel: {
          ...state.integracaoExcel,
          modoDeBuscaDoAtivo: action.modoDeBusca,
        },
      };
    }

    case "SELECIONAR_ESCALA_MOEDA": {
      return setCampoEdicaoMedida(state, "escala", action.escalaMoeda);
    }

    case "DEFINIR_COMPARTILHAMENTO_DE_ARQUIVOS": {
      return {
        ...state,
        arquivosMedidas: {
          ...state.arquivosMedidas,
          permiteCompartilhamento: action.permitido,
        },
      };
    }

    case "SAVE_GRID_STATE": {
      const { meta: type, payload } = action;

      switch (type) {
        case "aggregation":
          return set(gridAggregationState, payload, state);
        case "filter":
          return set(gridFilterState, payload, state);
        case "group":
          return set(gridGroupState, payload, state);
        case "sort":
          return set(gridSortState, payload, state);
        case "order":
          return set(gridOrderState, payload, state);
        case "fixedLeft":
          return set(gridFixedLeftState, payload, state);
        case "fixedRight":
          return set(gridFixedRightState, payload, state);
        default:
          return state;
      }
    }

    case "RESTORE_GRID_STATE": {
      const { formaDeVisualizacaoRelatorioSeries, ...grid } = action.payload;
      return compose(
        set(gridState, grid),
        set(
          modoVisualizacaoSeriesLens,
          formaDeVisualizacaoRelatorioSeries ||
            FORMA_VISUALIZACAO_SERIES_POR_ATIVOS
        )
      )(state);
    }

    case "RESET_GRID_STATE": {
      return set(gridState, initialState.gridState, state);
    }

    case "SET_IS_AXIS": {
      return set(isAxisLens, action.payload, state);
    }

    case "GERAR_RELATORIO": {
      return { ...state, gerandoRelatorio: true };
    }

    case "RELATORIO_GERADO": {
      return {
        ...state,
        gerandoRelatorio: false,
        alterouGrupamentoMedidaSelecionado: false,
      };
    }

    case "RESTAURAR_CONFIGURACOES_RELATORIO": {
      return { ...state, restaurandoConfiguracoes: true };
    }

    case "CONFIGURACOES_RESTAURADAS": {
      return { ...state, restaurandoConfiguracoes: false };
    }

    case "SELECIONAR_CONFIGURACAO_SERIES": {
      const configuracaoGlobalRelatorio = action.configuracaoGlobalRelatorio;
      return {
        ...state,
        configuracaoSeries: {
          ...state.configuracaoSeries,
          dataBase: configuracaoGlobalRelatorio.dataBase,
          identificadores: configuracaoGlobalRelatorio.identificadoresAtivos,
          exibirDatasSemNegociacao:
            configuracaoGlobalRelatorio.exibirDatasSemNegociacao,
        },
      };
    }

    case "SET_CONFIGURACOES_RELATORIO_SALVO": {
      return {
        ...state,
        configuracoesRelatorioSalvo: action.configuracaoGlobalRelatorio,
      };
    }

    case "SELECIONAR_CASAS_DECIMAIS_MEDIDA": {
      return setCampoEdicaoMedida(state, "casasDecimais", action.casasDecimais);
    }

    case "SELECIONAR_MODO_VISUALIZACAO_SERIES": {
      return compose(
        state => reducer(state, resetGridState()),
        set(modoVisualizacaoSeriesLens, action.modoVisualizacao)
      )(state);
    }

    case "SELECIONAR_MODO_EXIBIR_DATAS_SEM_NEGOCIACAO_SERIES": {
      return {
        ...state,
        configuracaoSeries: {
          ...state.configuracaoSeries,
          exibirDatasSemNegociacao: action.exibir,
        },
      };
    }

    case "SELECIONAR_IDENTIFICADOR_SERIES": {
      return {
        ...state,
        configuracaoSeries: {
          ...state.configuracaoSeries,
          identificadores: state.configuracaoSeries.identificadores
            .filter(i => i !== action.identificador)
            .concat(action.identificador),
        },
      };
    }

    case "DESELECIONAR_IDENTIFICADOR_SERIES": {
      return {
        ...state,
        configuracaoSeries: {
          ...state.configuracaoSeries,
          identificadores: state.configuracaoSeries.identificadores.filter(
            i => i !== action.identificador
          ),
        },
      };
    }

    default:
      return state;
  }
}

function getPrimeiroIndice(state: State): string {
  if (state.dominio.indices && state.dominio.indices.length > 0) {
    return state.dominio.indices[0].nome;
  }
  return "";
}

function getPrimeiroMesAnoComposto(state: State): string {
  if (
    state.dominio.mesesEAnosCompostos &&
    state.dominio.mesesEAnosCompostos.length > 0
  ) {
    return state.dominio.mesesEAnosCompostos[0].nome;
  }
  return "";
}

function alteraDataBaseMedidas(
  state: State,
  dataBase: string
): Array<MedidaSelecionada> {
  const medidaTemDataBase = has("dataBase");
  const medidaDeveSerAtualizada = propNotEq(
    "periodo",
    ID_PERIODO_NOS_ULTIMOS_4_MESES
  );

  return (getMedidasSelecionadas(state) || []).map(medida =>
    both(medidaTemDataBase, medidaDeveSerAtualizada)(medida)
      ? assoc("dataBase", dataBase, medida)
      : medida
  );
}

function filtrarMedida(
  medidasSelecionadas: Array<MedidaSelecionada>,
  idMedidaConfiguracaoRelatorio: Id
): Array<MedidaSelecionada> {
  return medidasSelecionadas.filter(
    complement(
      pathEq(
        ["medida", "idConfiguracaoRelatorio"],
        idMedidaConfiguracaoRelatorio
      )
    )
  );
}
